import * as React from 'react';
import styled, { css } from 'styled-components';

import type { Event } from 'components/events/events/types';
import { Icon, IfPermitted } from 'components/common';
import useLicenseCheck from 'license/useLicenseCheck';
import { useInvestigationDrawer } from 'security-app/components/Investigations/contexts';
import { useAddEvidence, useGetInvestigationDetails } from 'security-app/hooks/useInvestigationsAPI';

import AddEvidenceModal from './shared/AddEvidenceModal';

const EvidenceActionButton = styled.a<{ $disabled?: boolean }>(({ $disabled, theme }) => css`
  display: flex;
  align-items: center;
  color: ${$disabled ? theme.colors.gray[90] : 'inherit'};
  text-decoration: none;
  gap: 6px;
  cursor: ${$disabled ? 'not-allowed' : 'pointer'};

  &:hover {
    color: ${$disabled ? theme.colors.gray[90] : 'default'};
  }

  &:visited {
    color: inherit;
  }
`);

const InvestigationsEventAction = ({ event }: { event: Event }) => {
  const addEvidenceModalRef = React.useRef(null);
  const { security } = useLicenseCheck();

  const { selectedInvestigationId, setCollapsed } = useInvestigationDrawer();
  const { currentInvestigation } = useGetInvestigationDetails(selectedInvestigationId);

  const { addEvidence } = useAddEvidence();

  const disabledAdd = !currentInvestigation || currentInvestigation?.archived;

  const onAddEvent = async () => {
    if (disabledAdd) return;

    await addEvidence({
      invId: selectedInvestigationId,
      payload: { events: [event.id] },
    });

    setCollapsed(false);
  };

  return security?.isValid && (
    <IfPermitted permissions="investigations:edit">
      <dd>
        <EvidenceActionButton onClick={onAddEvent} $disabled={disabledAdd}>
          Add to investigation <Icon name="puzzle-piece" size="sm" />
        </EvidenceActionButton>
      </dd>
      <dd>
        <EvidenceActionButton onClick={() => addEvidenceModalRef.current.toggle()}>
          Select an investigation <Icon name="puzzle-piece" size="sm" />
        </EvidenceActionButton>
      </dd>
      <AddEvidenceModal id={event.id} type="events" ref={addEvidenceModalRef} />
    </IfPermitted>
  );
};

export default InvestigationsEventAction;
